<script setup>
import {
  Bell,
  Building,
  Calendar,
  DollarSign,
  LayoutDashboard,
  LogOut,
  MapPin,
  Settings,
  SquareParking,
  Ticket,
  Car,
  Menu,
  User,
  Users,
  X
} from 'lucide-vue-next';
import { accessOwnerDashboard, accessOwnerPlusManager } from '~/lib/utils';

const props = defineProps({
  pageTitle: {
    type: String,
    required: true,
    default: 'ADD PAGE TITLE'
  }
});

const config = useRuntimeConfig();
const { getUser } = useUser();
const isOpen = ref(false);

const isAvailable = () => {
  return (
    accessOwnerPlusManager(getUser.role) && usePlan().isPlatinum)||
    (config?.public?.isMain && accessOwnerPlusManager(getUser.role)
  );
}

const navItems = ref([
  {
    title: 'Book A Spot',
    url: '/search?facility=all',
    icon: MapPin,
    isActive: !accessOwnerDashboard(getUser.role),
  },
  {
    title: 'My Reservations',
    url: '/dashboard',
    icon: Calendar,
    isActive: true,
  },
  {
    title: 'Facilities',
    url: '/dashboard/facilities',
    icon: Building,
    isActive: isAvailable(),
  },
  {
    title: 'Lots',
    url: '/dashboard/lots',
    icon: SquareParking,
    isActive: isAvailable(),
  },
  {
    title: 'Team',
    url: '/dashboard/team',
    icon: Users,
    isActive: isAvailable(),
  },
  {
    title: 'My Wallet',
    url: '/dashboard/wallet',
    icon: DollarSign,
    isActive: getUser.role === 'PARKER',
  },
  {
    title: 'My Vehicles',
    url: '/dashboard/vehicles',
    icon: Car,
    isActive: getUser.role === 'PARKER',
  },
  {
    title: 'Discount Parking',
    url: '/parking-packs',
    icon: Ticket,
    isActive: !accessOwnerDashboard(getUser.role),
  },
]);

const toggleSidebar = () => {
  isOpen.value = !isOpen.value;
};

let resizeRafId = null

const handleResize = () => {
  if (resizeRafId) return
  resizeRafId = requestAnimationFrame(() => {
    if (window.innerWidth <= 768) {
      isOpen.value = false
    } else {
      isOpen.value = true
    }
    resizeRafId = null
  })
}

const handleLinkClick = () => {
  if (window.innerWidth < 768) {
    isOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true })
  isOpen.value = window.innerWidth >= 768;
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeRafId) {
    cancelAnimationFrame(resizeRafId)
    resizeRafId = null
  }
});

const logoClick = () => {
  if (getUser.role === 'PARKER') {
    navigateTo('/');
  } else {
    navigateTo('/dashboard');
  }
}

const userRole = (role) => {
  if (role === 'CPO' || role === 'PPO') return 'OWNER';
  else return role;
}
</script>

<template>
<div class="flex h-screen">
  <!-- Sidebar -->
  <div
    :class="[
      'fixed inset-y-0 left-0 z-50 w-64 transform bg-primary dark:bg-black shadow-lg transition-transform duration-300 md:relative md:translate-x-0 flex flex-col',
      isOpen ? 'w-full md:w-64' : '-translate-x-full'
    ]"
  >
    <!-- Sidebar Header -->
    <div class="flex items-center justify-between p-2 bg-primary dark:bg-black">
      <NuxtLink class="flex items-center cursor-pointer" @click="logoClick">
        <template v-if="config?.public?.isMain">
          <img src="/images/spotsurferLogo.png" alt="Spotsurfer Logo" class="h-12 w-12 mr-1" loading="lazy" />
          <!-- Hide when collapsed -->
          <div class="flex flex-col">
            <span class="text-3xl font-semibold text-white uppercase">
              Spotsurfer
            </span>
            <span class="text-white text-xs tracking-widest uppercase group-has-[[data-collapsible=icon]]/sidebar-wrapper:hidden">Reserve. Park. Play.</span>
          </div>
        </template>
        <template v-else>
          <NuxtImg
            class="max-h-16 p-2 w-full object-cover"
            style="min-width: 80px; max-width: 200px;"
            :src="globalConfig()?.logo?.url"
            alt="Logo"
          />
        </template>
      </NuxtLink>

      <button
        @click="toggleSidebar"
        v-if="isOpen"
        class="absolute top-2 right-2 p-2 rounded-md md:hidden text-white"
      >
        <X class="h-6 w-6" />
      </button>
    </div>

    <!-- Navigation Links -->
    <nav class="p-2">
      <ul class="space-y-2">
        <li v-for="(item, index) in navItems.filter((item) => item.isActive)" :key="index">
          <NuxtLink
            :href="item.url"
            @click="handleLinkClick(item)"
            :class="[
              'flex items-center p-2 text-white text-nav-link-default hover:text-nav-link-hover'
            ]"
          >
            <component :is="item.icon" class="h-5 w-5 mr-3" />
            <span>{{ item.title }}</span>
          </NuxtLink>
        </li>
      </ul>
    </nav>
    <div class="mt-auto">
      <span class="flex px-3 py-1 bg-orange-200 text-primary/80 text-xs w-[50%] font-semibold [clip-path:polygon(0_0,calc(100%-0.75rem)_0,100%_100%,0_100%)]">{{ userRole(getUser?.role) }}</span>
      <DropdownMenu>
        <DropdownMenuTrigger as-child  class="flex w-full items-center justify-between p-4">
          <button>
            <span class="flex h-6 w-6 items-center mr-2">
              <User class="stroke-white" v-if="!getUser?.avatar" />
              <NuxtImg class="h-full w-full rounded-full object-cover" :src="getUser?.avatar" v-else />
            </span>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-semibold text-white text-nav-link-default">{{ getUser?.first_name }} {{ getUser?.last_name }}</span>
              <span class="truncate text-xs text-white text-nav-link-default">{{ getUser?.email }}</span>
            </div>
            <Settings class="ml-2 size-4 text-white text-nav-link-default" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="bottom" align="end" :side-offset="4">
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ getUser?.first_name }} {{ getUser?.last_name }}</span>
                <span class="truncate text-xs">{{ getUser?.email }}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem class="p-0">
              <NuxtLink to="/dashboard/settings" class="flex items-center w-full p-2" @click="handleLinkClick">
                <Settings class="mr-2" />
                Settings
              </NuxtLink>
            </DropdownMenuItem>
            <DropdownMenuItem class="p-0">
              <NuxtLink to="/dashboard/support" class="flex items-center w-full p-2" @click="handleLinkClick">
                <Bell class="mr-2" />
                Support
              </NuxtLink>
            </DropdownMenuItem>
            <DropdownMenuItem v-if="getUser?.is_staff" class="p-0">
              <NuxtLink to="/admin" class="flex items-center w-full p-2" @click="handleLinkClick">
                <LayoutDashboard class="mr-2" />
                Admin Dashboard
              </NuxtLink>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="useLogout" class="cursor-pointer">
            <LogOut class="mr-2"/>
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>

  <!-- Main Content -->
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Top Navigation -->
    <header class="bg-white dark:bg-background z-10">
      <div class="flex justify-end px-4 items-center">
        <h1 class="p-2 text-3xl font-bold mr-auto">{{ pageTitle }}</h1>
        <button @click="toggleSidebar">
          <Menu :size="32" class="stroke-primary hover:stroke-nav-link-hover md:hidden" />
        </button>
      </div>
    </header>

    <!-- Content Area -->
    <main class="flex-1 overflow-y-auto">
      <div class="mx-auto">
        <slot />
      </div>
    </main>
  </div>

  <!-- Overlay for mobile -->
  <div
    v-if="isOpen"
    @click="toggleSidebar"
    class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
  ></div>
</div>
</template>
